import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import GridIcon from '@/assets/icons/grid.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import TableIcon from '@/assets/icons/table.svg?react';

import { FilterInput } from '@/components/elements/filter-input/filter-input.component';
import { SearchInput } from '@/components/elements/search-input/search-input.component';
import { T } from '@/components/i18n/message.i18n.component';
import MainLayout from '@/components/layout/main/main.layout.component';
import Button from '@/components/ui/button/button.component';
import { LifecycleStatus, Product } from '@/gql/graphql';
import { useProducts } from '@/hooks/use-products/user-products.hook';
import { useToast } from '@/hooks/use-toast.hook';
import ProductCard from '@/pages/products/components/product-card.component';
import { ProductForm } from '@/pages/products/products.form';
import { dialogTranslations, productPageMessages, toastTranslations } from '@/pages/products/products.messages';
import { allProducts, removeProduct, updateProduct } from '@/pages/products/products.requests';
import { useDialogStore } from '@/store/dialog.store';
import { useIntl } from 'react-intl';
import { ProductTable } from './components/product-table.component';
import { ProductsNoResults } from './products.no-results';
import { ProductSkeleton } from './products.skeleton';

export const ProductsPage = () => {
  const t = useIntl();
  const { toast } = useToast();
  const navigate = useNavigate();

  const { data } = useQuery<{ products: Product[] }>(allProducts);

  const {
    products,
    loading,
    search,
    filter,
    productsPageView,
    setProductsPageView,
    handleFilterChange,
    handleSearchChange,
  } = useProducts({
    initialProducts: data?.products,
  });

  const [editProductMutation] = useMutation(updateProduct);
  const [deleteProductMutation] = useMutation(removeProduct);

  const { openDialog, setDialogLoading, closeDialog } = useDialogStore((state) => ({
    loading: state.loading,
    openDialog: state.openDialog,
    closeDialog: state.closeDialog,
    setDialogLoading: state.setLoading,
  }));

  const handleProductAddDialog = () => {
    openDialog({
      type: 'custom',
      title: <T {...dialogTranslations.addProductTitle} />,
      element: (
        <ProductForm
          afterAdd={(result) => {
            const id = result.data?.createProduct.id;
            navigate(`/product/${id}/pre-tailoring`);
          }}
        />
      ),
    });
  };

  const openEditDialog = (product: Product) => {
    openDialog({
      type: 'custom',
      title: <T {...dialogTranslations.editProductTitle} />,
      element: <ProductForm edit initialValues={product as Product} />,
    });
  };

  const openConfirmationDialog = (product: Product) => {
    openDialog({
      type: 'confirmation',
      item: { id: product.id, name: product.name },
      onConfirm: async () => {
        setDialogLoading(true);
        await deleteProductMutation({
          variables: {
            id: product.id,
          },
          refetchQueries: [{ query: allProducts }],
        });
        closeDialog();
        toast({
          description: <T {...toastTranslations.deleteProductToast} />,
          variant: 'default',
        });
      },
    });
  };

  const onProductStatusChange = async (product: Product) => {
    if (product.lifecycleStatus === LifecycleStatus.Active) {
      openDialog({
        type: 'confirmation',
        title: <T {...dialogTranslations.retireProductTitle} />,
        confirmationText: t.formatMessage(dialogTranslations.retireProductDescription, {
          name: product.name,
        }),
        item: { id: product.id, name: product.name },
        onConfirm: async () => {
          setDialogLoading(true);
          await editProductMutation({
            variables: {
              updateProductInput: {
                id: product.id,
                name: product.name,
                description: product.description,
                version: product.version,
                productIdentifier: product.productIdentifier,
                projectIdentifier: product.projectIdentifier,
                lifecycleStatus: LifecycleStatus.Retired,
              },
            },
            refetchQueries: [{ query: allProducts }],
          });
          closeDialog();
          toast({
            description: <T {...toastTranslations.retireProductToast} />,
            variant: 'default',
          });
        },
      });
    } else {
      await editProductMutation({
        variables: {
          updateProductInput: {
            id: product.id,
            name: product.name,
            description: product.description,
            version: product.version,
            productIdentifier: product.productIdentifier,
            projectIdentifier: product.projectIdentifier,
            lifecycleStatus: LifecycleStatus.Active,
          },
        },
        refetchQueries: [{ query: allProducts }],
      });
      toast({
        description: <T {...toastTranslations.activateProductToast} />,
        variant: 'default',
      });
    }
  };

  const handleProductNavigation = (product: Product) => {
    navigate(`/product/${product.id}/pre-tailoring`);
  };

  const handleViewChange = () => {
    setProductsPageView(productsPageView === 'grid' ? 'table' : 'grid');
  };

  return (
    <MainLayout>
      <div>
        <div className="flex flex-row justify-between items-center mb-7">
          <h1 data-testid="product-page-title">
            <T {...productPageMessages.title} />
          </h1>
          <Button data-testid="product-page-add-product-button" onClick={handleProductAddDialog} variant="default">
            <PlusIcon className="w-5 mr-2" />
            <T {...productPageMessages.addProduct} />
          </Button>
        </div>
        <div className="mb-6 flex flex-row justify-between items-center">
          <SearchInput
            data-testid="product-page-search"
            placeholder={t.formatMessage(productPageMessages.searchBarPlaceholder)}
            onSearch={(e) => {
              const search = e.target.value;
              handleSearchChange(search);
            }}
          />
          <div className="flex flex-row items-center">
            <p className="mr-2 typo-secondary">
              <T {...productPageMessages.filterBarText} />
            </p>
            <FilterInput
              value={filter}
              options={[
                {
                  value: 'ALL',
                  label: <T {...productPageMessages.filterBarOptionAllProducts} />,
                  testId: 'filter-bar-option-all-products',
                },
                {
                  value: 'MINE',
                  label: <T {...productPageMessages.filterBarOptionMyProducts} />,
                  testId: 'filter-bar-option-my-products',
                },
              ]}
              data-testid="products-page-filter-bar"
              onFilterChange={handleFilterChange}
            />
            <Button data-testid="product-page-view-trigger" onClick={handleViewChange} size="small" variant="secondary">
              {productsPageView === 'grid' && (
                <>
                  <TableIcon className="w-5 mr-2" />
                  <T {...productPageMessages.tableViewButton} />
                </>
              )}
              {productsPageView === 'table' && (
                <>
                  <GridIcon className="w-5 mr-2" />
                  <T {...productPageMessages.gridViewButton} />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <ProductsNoResults search={search} loading={loading} products={products} onProductAdd={handleProductAddDialog} />
      <div className="grid grid-cols-4 gap-3 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {loading && <ProductSkeleton data-testid="product-page-loading-skeleton" />}
        {!loading &&
          productsPageView === 'grid' &&
          products.map((product) => (
            <ProductCard
              onCardClick={handleProductNavigation}
              key={product.id}
              product={product}
              handleEdit={openEditDialog}
              handleDelete={openConfirmationDialog}
              handleStatusChange={onProductStatusChange}
            />
          ))}
      </div>
      {!loading && productsPageView === 'table' && (
        <ProductTable
          data={products}
          onRowClick={handleProductNavigation}
          handleEdit={openEditDialog}
          handleDelete={openConfirmationDialog}
          handleStatusChange={onProductStatusChange}
        />
      )}
    </MainLayout>
  );
};
